import React, { useMemo, useState, useEffect } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment-jalaali';
import PersianDatePicker from '../PersianDatePicker';
import { ValidationError, clientsApi, parse422Error } from '../../utils/api';
import { formatMoneyFa as formatMoney } from '../../utils/formatPriceToman';
import { formatJalaliApiDate } from '../../utils/dateUtils';
import { applyValidationErrorsToForm } from '../../utils/formHelpers';
import {
  calculateItemAmount,
  ITEM_DISCOUNT_TYPES,
} from '../../utils/itemDiscountUtils';

const { Option } = Select;
const { TextArea } = Input;

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();

const createEmptyItem = () => ({
  item_id: null,
  product_id: null,
  item_name: '',
  quantity: 1,
  unit_id: null,
  cost_per_item: 0,
  discount: 0,
  discount_type: 'percent',
  taxes: [],
  amount: 0,
  item_summary: '',
});

const EstimateForm = ({
  options,
  initialValues,
  initialItems,
  isEdit,
  onSubmit,
  submitLoading,
  lockClient = false,
  lockProject = false,
}) => {
  const [form] = Form.useForm();
  const [items, setItems] = useState(initialItems?.length ? initialItems : [createEmptyItem()]);
  const [clientDeals, setClientDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);

  useEffect(() => {
    if (initialValues) form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  useEffect(() => {
    if (initialItems?.length) setItems(initialItems);
  }, [initialItems]);

  const discountValue = Number(Form.useWatch('discount_value', form) || 0);
  const discountType = Form.useWatch('discount_type', form) || 'percent';
  const calculateTax = Form.useWatch('calculate_tax', form) || 'after_discount';
  const selectedClient = Form.useWatch('client_id', form);

  useEffect(() => {
    if (!selectedClient) {
      setClientDeals([]);
      form.setFieldsValue({ deal_id: undefined });
      return;
    }
    let cancelled = false;
    const loadClientDeals = async () => {
      setDealsLoading(true);
      try {
        const res = await clientsApi.getClientDeals(selectedClient);
        if (cancelled) return;
        const deals = res?.data?.deals || res?.deals || [];
        setClientDeals(deals);
        const currentDealId = form.getFieldValue('deal_id');
        if (currentDealId && !deals.some((d) => String(d.id) === String(currentDealId))) {
          form.setFieldsValue({ deal_id: undefined });
        }
      } catch (error) {
        if (!cancelled) {
          setClientDeals([]);
          message.error('خطا در دریافت معاملات مشتری');
        }
      } finally {
        if (!cancelled) setDealsLoading(false);
      }
    };
    loadClientDeals();
    return () => {
      cancelled = true;
    };
  }, [selectedClient, form]);

  const taxRateMap = useMemo(() => {
    const m = {};
    (options.taxes || []).forEach((t) => {
      m[t.id] = Number(t.rate_percent || 0);
    });
    return m;
  }, [options.taxes]);

  const subTotal = useMemo(() => items.reduce((sum, it) => sum + Number(it.amount || 0), 0), [items]);
  const discountAmount = useMemo(() => {
    if (discountType === 'percent') return (subTotal * discountValue) / 100;
    return Math.min(subTotal, discountValue);
  }, [discountType, discountValue, subTotal]);

  const totalTaxAmount = useMemo(() => {
    if (!items.length) return 0;
    const discountRatio = subTotal > 0 ? Math.max(0, (subTotal - discountAmount) / subTotal) : 1;
    return items.reduce((sum, it) => {
      const baseAmount = Number(it.amount || 0);
      const effectiveBase = calculateTax === 'after_discount' ? baseAmount * discountRatio : baseAmount;
      const rowTax = (it.taxes || []).reduce((rowSum, taxId) => {
        const rate = Number(taxRateMap[taxId] || 0);
        return rowSum + (effectiveBase * rate) / 100;
      }, 0);
      return sum + rowTax;
    }, 0);
  }, [items, calculateTax, subTotal, discountAmount, taxRateMap]);

  const total = useMemo(() => subTotal - discountAmount + totalTaxAmount, [subTotal, discountAmount, totalTaxAmount]);

  const setItemValue = (index, patch) => {
    setItems((prev) => {
      const next = [...prev];
      const current = { ...next[index], ...patch };
      if (current.discount_type === 'percent' && Number(current.discount) > 100) {
        current.discount = 100;
      }
      if (Number(current.discount) < 0) {
        current.discount = 0;
      }
      current.amount = calculateItemAmount(current);
      next[index] = current;
      return next;
    });
  };

  const addItemRow = () => setItems((prev) => [...prev, createEmptyItem()]);
  const removeItemRow = (index) => setItems((prev) => prev.filter((_, i) => i !== index));
  const isProductUsedInOtherRow = (productId, rowIndex) =>
    productId != null && items.some((item, index) => index !== rowIndex && String(item.product_id) === String(productId));

  const getValidationMessage = (error) => {
    if (error instanceof ValidationError) {
      return parse422Error({ error: { details: error.details } }) || error.message;
    }
    if (error?.details) {
      return parse422Error({ error: { details: error.details } }) || error.message;
    }
    return error?.message || 'خطا در ذخیره پیش‌فاکتور';
  };

  const showSubmitErrorMessage = (errorText) => {
    message.error({
      content: <span style={{ whiteSpace: 'pre-line' }}>{errorText}</span>,
      duration: 6,
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // محصول اختیاری است؛ آیتم دستی فقط با نام و تعداد معتبر است و product_id = null ارسال می‌شود
      const validItems = items.filter((it) => it.item_name && Number(it.quantity) > 0);
      if (!validItems.length) {
        message.warning('حداقل یک آیتم معتبر وارد کنید');
        return;
      }

      const duplicateProductId = validItems
        .map((item) => item.product_id)
        .filter((productId) => productId != null)
        .find((productId, index, arr) => arr.findIndex((id) => String(id) === String(productId)) !== index);
      if (duplicateProductId) {
        const productName = products.find((product) => String(product.id) === String(duplicateProductId))?.name || 'انتخاب‌شده';
        const errorText = `محصول «${productName}» بیش از یک بار در پیش‌فاکتور انتخاب شده است.`;
        message.warning(errorText);
        return;
      }

      const payload = {
        estimate_number: values.estimate_number,
        valid_till: formatJalaliApiDate(values.valid_till),
        currency_id: values.currency_id,
        client_id: values.client_id,
        deal_id: values.deal_id || null,
        project_id: values.project_id || null,
        calculate_tax: values.calculate_tax || 'after_discount',
        bank_account_id: values.bank_account_id || null,
        description: stripHtml(values.description),
        category_id: values.category_id || null,
        sort_order: validItems.map((it) => it.product_id ?? null),
        item_name: validItems.map((it) => it.item_name),
        quantity: validItems.map((it) => Number(it.quantity || 0)),
        product_id: validItems.map((it) => it.product_id ?? null),
        unit_id: validItems.map((it) => it.unit_id || null),
        cost_per_item: validItems.map((it) => Number(it.cost_per_item || 0)),
        item_discount: validItems.map((it) => Number(it.discount || 0)),
        item_discount_type: validItems.map((it) => it.discount_type || 'percent'),
        taxes: validItems.map((it) => it.taxes || []),
        amount: validItems.map((it) => Number(it.amount || 0)),
        item_summary: validItems.map((it) => it.item_summary || ''),
        invoice_item_image: [],
        sub_total: Number(subTotal.toFixed(2)),
        discount_value: Number((values.discount_value || 0).toString().trim() || 0),
        discount_type: values.discount_type || 'percent',
        total: Number(total.toFixed(2)),
        note: values.note || '',
      };

      if (isEdit) {
        payload.item_ids = validItems.map((it) => it.item_id).filter(Boolean);
        payload.status = values.status || 'waiting';
      } else {
        const digits = String(values.estimate_number || '').replace(/\D/g, '');
        if (digits) payload.original_estimate_number = digits;
      }

      await onSubmit(payload);
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError || error?.details) {
        showSubmitErrorMessage(getValidationMessage(error));
        applyValidationErrorsToForm(form, error);
      } else {
        const errorText = getValidationMessage(error);
        showSubmitErrorMessage(errorText);
      }
    }
  };

  const opts = options || {};
  const currencies = opts.currencies || [];
  const clients = opts.clients || [];
  const projects = opts.projects || [];
  const bankAccounts = opts.bankAccounts || [];
  const categories = opts.categories || [];
  const products = opts.products || [];
  const units = opts.units || [];
  const taxes = opts.taxes || [];

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ fontFamily: 'CustomFont' }}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Form.Item name="estimate_number" label="شماره پیش‌فاکتور" rules={[{ required: true, message: 'الزامی است' }]}>
          <Input disabled={isEdit} style={{ fontFamily: 'CustomFont' }} />
        </Form.Item>
        <Form.Item name="valid_till" label="اعتبار تا" rules={[{ required: true, message: 'الزامی است' }]}>
          <PersianDatePicker style={{ fontFamily: 'CustomFont' }} />
        </Form.Item>
        <Form.Item name="currency_id" label="واحد پول" rules={[{ required: true, message: 'الزامی است' }]}>
          <Select style={{ fontFamily: 'CustomFont' }}>{currencies.map((c) => <Option key={c.id} value={c.id}>{c.currency_symbol}</Option>)}</Select>
        </Form.Item>
        <Form.Item name="client_id" label="مشتری" rules={[{ required: true, message: 'الزامی است' }]}>
          <Select
            showSearch
            optionFilterProp="label"
            disabled={lockClient}
            style={{ fontFamily: 'CustomFont' }}
          >
            {clients.map((c) => {
              const name = c.name_salutation || c.name || '—';
              const mobile = c.mobile || c.cell || c.phone || c.client_details?.mobile || '';
              const label = mobile ? `${name} — ${mobile}` : name;
              return (
                <Option key={c.id} value={c.id} label={label}>
                  {label}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
        <Form.Item name="project_id" label="پروژه">
          <Select
            allowClear={!lockProject}
            disabled={lockProject}
            showSearch
            optionFilterProp="children"
            style={{ fontFamily: 'CustomFont' }}
          >
            {projects.map((p) => (
              <Option key={p.id} value={p.id}>
                {p.project_name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="deal_id" label="معامله">
          <Select
            allowClear
            loading={dealsLoading}
            disabled={!selectedClient}
            placeholder={selectedClient ? 'انتخاب معامله (اختیاری)' : 'ابتدا مشتری را انتخاب کنید'}
            showSearch
            optionFilterProp="label"
            style={{ fontFamily: 'CustomFont' }}
            options={clientDeals.map((d) => ({ value: d.id, label: d.name }))}
          />
        </Form.Item>
        <Form.Item name="bank_account_id" label="حساب بانکی">
          <Select allowClear style={{ fontFamily: 'CustomFont' }}>{bankAccounts.map((b) => <Option key={b.id} value={b.id}>{b.account_name} {b.bank_name ? `(${b.bank_name})` : ''}</Option>)}</Select>
        </Form.Item>
        <Form.Item name="category_id" label="دسته‌بندی">
          <Select allowClear style={{ fontFamily: 'CustomFont' }}>{categories.map((c) => <Option key={c.id} value={c.id}>{c.category_name}</Option>)}</Select>
        </Form.Item>
        <Form.Item name="calculate_tax" label="محاسبه مالیات">
          <Select style={{ fontFamily: 'CustomFont' }}>
            <Option value="after_discount">بعد از تخفیف</Option>
            <Option value="before_discount">قبل از تخفیف</Option>
          </Select>
        </Form.Item>
        <Form.Item name="discount_value" label="تخفیف کلی پیش‌فاکتور">
          <InputNumber className="w-full" min={0} style={{ fontFamily: 'CustomFont' }} />
        </Form.Item>
        <Form.Item name="discount_type" label="نوع تخفیف کلی">
          <Select style={{ fontFamily: 'CustomFont' }}>
            <Option value="percent">درصدی</Option>
            <Option value="fixed">ثابت</Option>
          </Select>
        </Form.Item>
        {isEdit && (
          <Form.Item name="status" label="وضعیت">
            <Select style={{ fontFamily: 'CustomFont' }}>
              <Option value="accepted">پذیرفته شده</Option>
              <Option value="waiting">در انتظار</Option>
              <Option value="declined">رد شده</Option>
            </Select>
          </Form.Item>
        )}
      </div>

      <Form.Item name="description" label="توضیحات">
        <TextArea rows={2} style={{ fontFamily: 'CustomFont' }} />
      </Form.Item>

      <Card size="small" title={<span style={{ fontFamily: 'CustomFont' }}>آیتم‌ها</span>} className="mb-3">
        {items.map((it, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2 p-2 border rounded dark:border-gray-600">
            <div className="md:col-span-3">
              <Select
                className="w-full"
                placeholder="محصول (اختیاری)"
                allowClear
                value={it.product_id}
                onChange={(val) => {
                  if (val == null) {
                    setItemValue(index, { product_id: null });
                    return;
                  }
                  const p = products.find((x) => x.id === val);
                  setItemValue(index, {
                    product_id: val,
                    item_name: p?.name || it.item_name,
                    cost_per_item: Number(p?.price || 0),
                    unit_id: p?.unit_id || it.unit_id,
                  });
                }}
                showSearch
                optionFilterProp="children"
                style={{ fontFamily: 'CustomFont' }}
              >
                {products.map((p) => (
                  <Option key={p.id} value={p.id} disabled={isProductUsedInOtherRow(p.id, index)}>
                    {p.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="md:col-span-2">
              <Input placeholder="نام آیتم" value={it.item_name} onChange={(e) => setItemValue(index, { item_name: e.target.value })} style={{ fontFamily: 'CustomFont' }} />
            </div>
            <div className="md:col-span-1">
              <InputNumber className="w-full" min={1} value={it.quantity} onChange={(v) => setItemValue(index, { quantity: Number(v || 0) })} placeholder="تعداد" />
            </div>
            <div className="md:col-span-1">
              <Select className="w-full" placeholder="واحد" value={it.unit_id} onChange={(v) => setItemValue(index, { unit_id: v })} style={{ fontFamily: 'CustomFont' }}>
                {units.map((u) => <Option key={u.id} value={u.id}>{u.unit_type}</Option>)}
              </Select>
            </div>
            <div className="md:col-span-2">
              <InputNumber
                className="w-full"
                min={0}
                value={it.cost_per_item}
                onChange={(v) => setItemValue(index, { cost_per_item: Number(v || 0) })}
                placeholder="قیمت واحد"
              />
            </div>
            <div className="md:col-span-1">
              <InputNumber
                className="w-full"
                min={0}
                max={it.discount_type === 'percent' ? 100 : undefined}
                value={it.discount}
                onChange={(v) => setItemValue(index, { discount: Number(v || 0) })}
                placeholder="تخفیف"
              />
            </div>
            <div className="md:col-span-1">
              <Select
                className="w-full"
                value={it.discount_type || 'percent'}
                onChange={(v) => setItemValue(index, { discount_type: v })}
                style={{ fontFamily: 'CustomFont' }}
              >
                {ITEM_DISCOUNT_TYPES.map((t) => (
                  <Option key={t.value} value={t.value}>{t.label}</Option>
                ))}
              </Select>
            </div>
            <div className="md:col-span-1">
              <Popconfirm title="حذف آیتم؟" onConfirm={() => removeItemRow(index)} okText="بله" cancelText="خیر">
                <Button danger icon={<DeleteOutlined />} disabled={items.length === 1} />
              </Popconfirm>
            </div>
            <div className="md:col-span-3">
              <Select
                mode="multiple"
                className="w-full"
                placeholder="مالیات"
                value={it.taxes}
                onChange={(v) => setItemValue(index, { taxes: v })}
                style={{ fontFamily: 'CustomFont' }}
              >
                {taxes.map((t) => <Option key={t.id} value={t.id}>{t.tax_name} ({t.rate_percent}%)</Option>)}
              </Select>
            </div>
            <div className="md:col-span-3 flex items-center text-xs text-slate-500 tabular-nums" style={{ fontFamily: 'CustomFont' }}>
              مبلغ ردیف: <b className="mr-1 text-slate-800 dark:text-slate-100">{formatMoney(it.amount)}</b>
            </div>
            <div className="md:col-span-12">
              <Input placeholder="خلاصه آیتم" value={it.item_summary} onChange={(e) => setItemValue(index, { item_summary: e.target.value })} style={{ fontFamily: 'CustomFont' }} />
            </div>
          </div>
        ))}
        <Button icon={<PlusOutlined />} onClick={addItemRow} style={{ fontFamily: 'CustomFont' }}>افزودن آیتم</Button>
      </Card>

      <Card size="small" className="mb-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm" style={{ fontFamily: 'CustomFont' }}>
          <div>جمع پس از تخفیف آیتم‌ها: <b>{formatMoney(subTotal)}</b></div>
          <div>تخفیف کلی پیش‌فاکتور: <b>{formatMoney(discountAmount)}</b></div>
          <div>مالیات: <b>{formatMoney(totalTaxAmount)}</b></div>
          <div className="md:col-span-3">مبلغ نهایی: <b>{formatMoney(total)}</b></div>
        </div>
      </Card>

      <Form.Item name="note" label="یادداشت">
        <TextArea rows={2} style={{ fontFamily: 'CustomFont' }} />
      </Form.Item>

      <div className="flex justify-end gap-2">
        <Button onClick={() => window.history.back()} style={{ fontFamily: 'CustomFont' }}>انصراف</Button>
        <Button type="primary" htmlType="submit" className="bg-green-600 hover:bg-green-700 border-0" loading={submitLoading} style={{ fontFamily: 'CustomFont' }}>
          {isEdit ? 'ذخیره تغییرات' : 'ایجاد پیش‌فاکتور'}
        </Button>
      </div>
    </Form>
  );
};

export default EstimateForm;
